import { Box, HStack, Icon, Image, Text, VStack } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowUpRight, Sparkles, Network, Database, Flame, Bot, Fingerprint, Radar, Heart, MessageCircle, Instagram, Facebook, Music2, Image as ImageIcon, CircleStop } from 'lucide-react'
import { useCallback, useEffect, useState, type ElementType } from 'react'
import type { KrakenSocialPost } from '../../../../types/kraken.types'
import ReactMarkdown from 'react-markdown'
import { useNavigate } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import { KRAKEN_PRIMARY } from './KrakenChatBar'
import { useAuth } from '../../../../hooks/useAuth'

export interface Message {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: Date
  suggestions?: Array<{ label: string; prompt: string; reasoning: string; type: 'prompt' | 'investigate-online'; tools?: string[] }>
  /** Developer-only: tool categories this response invoked (graph/database/firecrawl/apify/infra/searchapi/osint). */
  toolsUsed?: string[]
  /** Scraped social posts (Apify) → rendered as an image grid. */
  socialPosts?: KrakenSocialPost[]
  /** Stream was aborted by the user — content is whatever arrived before the stop. */
  stopped?: boolean
}

const PLATFORM_ICON: Record<string, ElementType> = {
  instagram: Instagram,
  facebook: Facebook,
  tiktok: Music2,
}

const _fmtCount = (n?: number | null): string => {
  if (n == null) return ''
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`
  return String(n)
}

// Grid of scraped social posts: square thumbnails, hover reveals caption +
// engagement, click opens the post. Fed by the `social_posts` SSE event.
const SocialPostGrid = ({ posts }: { posts: KrakenSocialPost[] }) => {
  const shown = (posts || []).filter((p) => p.image || p.url || p.caption)
  if (shown.length === 0) return null
  return (
    <Box mb="12px">
      <Text fontSize="10px" fontWeight={700} color="gray.400" letterSpacing="0.06em" textTransform="uppercase" mb="8px">
        Social posts · {shown.length}
      </Text>
      <Box display="flex" flexWrap="wrap" gap="6px">
        {shown.map((p, i) => {
          const PlatIcon = PLATFORM_ICON[(p.platform || '').toLowerCase()]
          return (
            <Box
              key={i}
              as="a"
              {...(p.url ? { href: p.url, target: '_blank', rel: 'noopener noreferrer' } : {})}
              position="relative"
              w="120px"
              h="120px"
              flexShrink={0}
              borderRadius="10px"
              overflow="hidden"
              bg="gray.100"
              border="1px solid"
              borderColor="gray.200"
              cursor={p.url ? 'pointer' : 'default'}
              _hover={{ borderColor: 'purple.300' }}
              transition="border-color 0.15s ease"
              // Scope hover to THIS tile's own class so hovering one tile only
              // reveals its own overlay (role=group/_groupHover matched a shared
              // ancestor and lit up every tile at once).
              sx={{ '&:hover .kraken-post-overlay': { opacity: 1 } }}
            >
              {p.image ? (
                <Image
                  src={p.image}
                  alt={p.caption || 'social post'}
                  referrerPolicy="no-referrer"
                  w="120px"
                  h="120px"
                  objectFit="cover"
                  display="block"
                  fallback={
                    <Box w="120px" h="120px" display="flex" alignItems="center" justifyContent="center" p="6px" bg="gray.50">
                      <Text fontSize="9px" color="gray.500" noOfLines={4} textAlign="center">{p.caption || 'post'}</Text>
                    </Box>
                  }
                />
              ) : (
                <Box w="120px" h="120px" display="flex" alignItems="center" justifyContent="center" p="6px" bg="gray.50">
                  <Icon as={ImageIcon} w="16px" h="16px" color="gray.400" />
                </Box>
              )}

              {PlatIcon && (
                <Box position="absolute" top="4px" left="4px" bg="blackAlpha.600" borderRadius="full" p="3px" display="flex">
                  <Icon as={PlatIcon} w="10px" h="10px" color="white" />
                </Box>
              )}

              {/* hover overlay: caption + engagement (per-tile, see sx above) */}
              <Box
                className="kraken-post-overlay"
                position="absolute"
                top={0} right={0} bottom={0} left={0}
                bg="blackAlpha.700"
                opacity={0}
                transition="opacity 0.15s ease"
                p="7px"
                display="flex"
                flexDirection="column"
                justifyContent="flex-end"
                color="white"
              >
                {p.caption && (
                  <Text fontSize="10px" lineHeight="1.35" noOfLines={4} mb="5px">{p.caption}</Text>
                )}
                <HStack spacing="10px" fontSize="10px" fontWeight={700}>
                  {p.likes != null && (
                    <HStack spacing="3px"><Icon as={Heart} w="10px" h="10px" /><Text>{_fmtCount(p.likes)}</Text></HStack>
                  )}
                  {p.comments != null && (
                    <HStack spacing="3px"><Icon as={MessageCircle} w="10px" h="10px" /><Text>{_fmtCount(p.comments)}</Text></HStack>
                  )}
                </HStack>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

// Developer-only tool-category icons. Colours echo the tool's "source", and
// each renders the real lucide glyph (with a native tooltip) instead of a text tag.
const TOOL_META: Record<string, { icon: ElementType; bg: string; fg: string; label: string }> = {
  graph: { icon: Network, bg: '#faf5ff', fg: '#6b21a8', label: 'Graph' },
  database: { icon: Database, bg: '#eff6ff', fg: '#1e40af', label: 'Database' },
  firecrawl: { icon: Flame, bg: '#fff7ed', fg: '#9a3412', label: 'Firecrawl' },
  apify: { icon: Bot, bg: '#ecfdf5', fg: '#065f46', label: 'Apify' },
  infra: { icon: Fingerprint, bg: '#ecfeff', fg: '#155e75', label: 'Infrastructure' },
  searchapi: { icon: ImageIcon, bg: '#f0fdfa', fg: '#0f766e', label: 'Reverse image' },
  osint: { icon: Radar, bg: '#fdf2f8', fg: '#be185d', label: 'OSINT' },
}

const ToolBadges = ({ tools, label }: { tools?: string[]; label?: string }) => {
  if (!tools || tools.length === 0) return null
  return (
    <HStack spacing="4px" flexWrap="wrap" rowGap="4px" mt="6px" align="center">
      {label && (
        <Text fontSize="9px" fontWeight={700} color="gray.400" textTransform="uppercase" letterSpacing="0.06em">
          {label}
        </Text>
      )}
      {tools.map((t, i) => {
        const m = TOOL_META[t] ?? { icon: Sparkles, bg: '#f1f5f9', fg: '#475569', label: t }
        return (
          <Box
            key={i}
            title={m.label}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            w="18px"
            h="18px"
            borderRadius="full"
            bg={m.bg}
            color={m.fg}
            border="1px solid"
            borderColor={`${m.fg}22`}
          >
            <Icon as={m.icon} w="11px" h="11px" strokeWidth={2.5} />
          </Box>
        )
      })}
    </HStack>
  )
}

interface KrakenChatMessageProps {
  message: string
  isUser: boolean
  isStreaming?: boolean
  onSendMessage?: (message: string) => void
  /**
   * Called when the user clicks a special `[[suggestion:…|investigate-online:<vertex_id>]]`
   * chip. Triggers the dedicated discovery pipeline instead of sending the
   * label back as a normal chat message.
   */
  onInvestigateOnline?: (vertexId: string) => void
  /** Structured follow-up suggestions generated by the suggestion LLM. */
  suggestions?: Message['suggestions']
  /** Developer-only: tool categories this response invoked. */
  toolsUsed?: string[]
  /** Scraped social posts → image grid. */
  socialPosts?: KrakenSocialPost[]
  /** Stream was stopped by the user → show a "Stopped" marker under the partial content. */
  stopped?: boolean
  /**
   * Static render for PDF export: disables entrance animation, the streaming
   * cursor, the thinking chain, and suggestion chips. Entity chips + score
   * bars keep their full visual styling but become non-interactive (no-op
   * click, default cursor). Tables/lists/code/images render unchanged via
   * the same `markdownComponents`.
   */
  printMode?: boolean
}

const MotionBox = motion(Box)

const ENTITY_PREFIX_RE = /^(seller_|item_|saw_|entity_|product_|seizure_|subcase_|case_|contact_|registrar_|registrant_|isp_)/

// Visual style per entity type — colors match the network node palette
// so a chip in the chat reads as the same "thing" as a node in the graph.
const TYPE_STYLES: Record<string, { label: string; bg: string; fg: string; border: string }> = {
  seller:        { label: 'Seller',  bg: '#faf5ff', fg: '#4c1d95', border: '#a78bfa55' },
  item:          { label: 'Item',    bg: '#eff6ff', fg: '#1e3a8a', border: '#60a5fa55' },
  saw:           { label: 'Site',    bg: '#f0fdfa', fg: '#134e4a', border: '#5eead455' },
  website:       { label: 'Site',    bg: '#f0fdfa', fg: '#134e4a', border: '#5eead455' },
  entity:        { label: 'Entity',  bg: '#fff7ed', fg: '#9a3412', border: '#fdba7455' },
  customentity:  { label: 'Entity',  bg: '#fff7ed', fg: '#9a3412', border: '#fdba7455' },
  product:       { label: 'Product', bg: '#fefce8', fg: '#854d0e', border: '#facc1555' },
  customproduct: { label: 'Product', bg: '#fefce8', fg: '#854d0e', border: '#facc1555' },
  seizure:       { label: 'Seizure', bg: '#fef2f2', fg: '#991b1b', border: '#fca5a555' },
  customseizure: { label: 'Seizure', bg: '#fef2f2', fg: '#991b1b', border: '#fca5a555' },
  case:          { label: 'Case',     bg: '#eff6ff', fg: '#1e3a8a', border: '#93c5fd55' },
  subcase:       { label: 'Sub-case', bg: '#eff6ff', fg: '#1e3a8a', border: '#93c5fd55' },
  contact:       { label: 'Contact',  bg: '#eff6ff', fg: '#1e3a8a', border: '#93c5fd55' },
  registrar:     { label: 'Registrar',  bg: '#fff1f2', fg: '#881337', border: '#fda4af55' },
  registrant:    { label: 'Registrant', bg: '#fffbeb', fg: '#78350f', border: '#fcd34d55' },
  isp:           { label: 'ISP',        bg: '#ecfeff', fg: '#164e63', border: '#67e8f955' },
}

const EntityLink = ({
  type,
  id,
  name,
  onClick,
  printMode = false,
}: {
  type: string
  id: string
  name?: string
  onClick: () => void
  printMode?: boolean
}) => {
  const isSuggestion = type === 'suggestion'
  const displayName = isSuggestion ? id : (name || id.replace(ENTITY_PREFIX_RE, ''))
  const shortId = id.replace(ENTITY_PREFIX_RE, '').slice(0, 8)
  const style = TYPE_STYLES[type.toLowerCase()] ?? {
    label: type.charAt(0).toUpperCase() + type.slice(1),
    bg: '#f8fafc',
    fg: '#334155',
    border: '#cbd5e155',
  }

  if (isSuggestion) {
    return (
      <Box
        as="button"
        type="button"
        display="inline-flex"
        alignItems="center"
        gap="6px"
        color="purple.700"
        fontWeight={500}
        fontSize="13px"
        lineHeight="1.2"
        cursor={printMode ? 'default' : 'pointer'}
        bg="white"
        px="10px"
        py="6px"
        my="3px"
        mr="6px"
        borderRadius="999px"
        border="1px solid"
        borderColor="purple.200"
        boxShadow="0 1px 2px rgba(124, 58, 237, 0.06)"
        _hover={
          printMode
            ? undefined
            : {
                borderColor: 'purple.400',
                bg: 'purple.50',
                color: 'purple.800',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 10px -4px rgba(124, 58, 237, 0.25)',
              }
        }
        _active={printMode ? undefined : { transform: 'translateY(0)', boxShadow: '0 1px 2px rgba(124, 58, 237, 0.1)' }}
        transition="all 0.15s ease"
        onClick={onClick}
        title={`Send: ${name ?? id}`}
        textAlign="left"
        whiteSpace="normal"
        maxW="100%"
      >
        <Icon as={Sparkles} w="12px" h="12px" color="purple.400" flexShrink={0} />
        <Text as="span" noOfLines={2}>
          {displayName}
        </Text>
        <Icon as={ArrowUpRight} w="13px" h="13px" color="purple.400" flexShrink={0} />
      </Box>
    )
  }

  return (
    <Box
      as="button"
      type="button"
      display="inline-flex"
      alignItems="center"
      gap="6px"
      bg={style.bg}
      color={style.fg}
      border="1px solid"
      borderColor={style.border}
      borderRadius="999px"
      px="10px"
      py="2px"
      mx="2px"
      fontWeight={500}
      fontSize="12.5px"
      lineHeight="18px"
      verticalAlign="baseline"
      cursor={printMode ? 'default' : 'pointer'}
      whiteSpace="nowrap"
      maxW="100%"
      _hover={printMode ? undefined : { filter: 'brightness(0.96)', borderColor: style.fg + '88' }}
      _active={printMode ? undefined : { transform: 'translateY(0.5px)' }}
      transition="all 0.15s ease"
      onClick={onClick}
      title={`Open ${style.label}: ${displayName} (${id})`}
    >
      <Box
        as="span"
        flexShrink={0}
        fontSize="9.5px"
        fontWeight={700}
        letterSpacing="0.06em"
        textTransform="uppercase"
        bg={style.fg + '14'}
        color={style.fg}
        borderRadius="999px"
        px="6px"
        py="0"
        lineHeight="14px"
      >
        {style.label}
      </Box>
      <Box
        as="span"
        flex="1 1 auto"
        minW={0}
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {name ? displayName : shortId + '…'}
      </Box>
      <Icon as={ArrowUpRight} w="10px" h="10px" opacity={0.55} flexShrink={0} />
    </Box>
  )
}

const ScoreBar = ({ value }: { value: number }) => {
  const color = value >= 70 ? '#22c55e' : value >= 40 ? '#eab308' : '#ef4444'
  return (
    <Box
      as="span"
      display="inline-block"
      verticalAlign="middle"
      w="28px"
      h="6px"
      bg="gray.100"
      borderRadius="3px"
      overflow="hidden"
      mx="2px"
    >
      <Box
        as="span"
        display="block"
        h="100%"
        w={`${Math.min(Math.max(value, 0), 100)}%`}
        bg={color}
        borderRadius="3px"
      />
    </Box>
  )
}

const parseEntityLinks = (
  text: string,
  onEntityClick: (type: string, id: string, name?: string) => void,
  printMode = false,
): React.ReactNode[] => {
  const parts: React.ReactNode[] = []
  // Match the literal `|` OR the masked SAFE_PIPE we substituted before
  // running markdown. After matching, we'll unmask any leftover SAFE_PIPE
  // characters in the captured groups so the rest of the pipeline sees
  // normal text. The character class excludes `]` and both pipe variants.
  const regex = new RegExp(
    `\\[\\[(\\w+):([^\\]|${SAFE_PIPE}]+)(?:[|${SAFE_PIPE}]([^\\]]+))?\\]\\]`,
    'g',
  )
  const unmask = (s: string | undefined) =>
    s ? s.replaceAll(SAFE_PIPE, '|').replaceAll(SAFE_URL, '').replace(/\\+$/, '') : s
  let lastIndex = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const [, type, rawId, rawName] = match
    const id = unmask(rawId) ?? rawId
    const name = unmask(rawName)
    if (type === 'score') {
      parts.push(<ScoreBar key={`score-${match.index}`} value={Number(id)} />)
    } else {
      parts.push(
        <EntityLink
          key={`${id}-${match.index}`}
          type={type}
          id={id}
          name={name}
          onClick={() => onEntityClick(type, id, name)}
          printMode={printMode}
        />,
      )
    }

    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

const ThinkingChain = ({ steps }: { steps: string[] }) => {
  return (
    <Box mb={3} position="relative" pl={1}>
      {/* Vertical connecting line through bullet centers */}
      {steps.length > 1 && (
        <Box
          position="absolute"
          left="7.5px"
          top="14px"
          bottom="14px"
          w="1px"
          transform="translateX(-50%)"
          bg="neutral.200"
          opacity={0.5}
        />
      )}

      <VStack align="stretch" gap={0}>
        {steps.map((step, index) => {
          const isActive = index === steps.length - 1
          const isCompleted = index < steps.length - 1

          return (
            <MotionBox
              key={index}
              display="flex"
              alignItems="center"
              gap={2.5}
              h="28px"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: index * 0.05 }}
            >
              {/* Bullet — fixed size, vertically centered */}
              <Box
                w="7px"
                h="7px"
                borderRadius="full"
                bg={isActive ? 'neutral.500' : 'neutral.300'}
                opacity={isCompleted ? 0.4 : 1}
                flexShrink={0}
                zIndex={1}
              />

              <HStack gap={1.5} align="center" minW={0} flex="1 1 auto" overflow="hidden">
                <Text
                  fontSize="13px"
                  color={isActive ? 'neutral.600' : 'neutral.400'}
                  fontWeight={isActive ? 500 : 400}
                  lineHeight="1"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  css={
                    isActive
                      ? {
                          animation: 'breatheText 2.5s ease-in-out infinite',
                          '@keyframes breatheText': {
                            '0%, 100%': { opacity: 0.6 },
                            '50%': { opacity: 1 },
                          },
                        }
                      : undefined
                  }
                >
                  {step}
                </Text>

                {isActive && (
                  <HStack gap="2px" ml={0.5}>
                    {[0, 1, 2].map((i) => (
                      <MotionBox
                        key={i}
                        w="3px"
                        h="3px"
                        borderRadius="full"
                        bg="neutral.400"
                        animate={{
                          opacity: [0.2, 0.7, 0.2],
                          scale: [0.8, 1.1, 0.8],
                        }}
                        transition={{
                          duration: 1.4,
                          repeat: Infinity,
                          delay: i * 0.2,
                          ease: 'easeInOut',
                        }}
                      />
                    ))}
                  </HStack>
                )}
              </HStack>
            </MotionBox>
          )
        })}
      </VStack>
    </Box>
  )
}

// Escape `|` and `]` inside [[Type:id|name]] markers BEFORE markdown parsing.
// Otherwise markdown's GFM table parser eats the `|` (treats it as a cell
// separator), splitting the marker across two cells:
//   `[[Entity:entity_xxx | Name]]` → cell A: `[[Entity:entity_xxx`,
//                                    cell B: `Name]]`
// The chip never parses. We swap `|` and inner `]` to private-use Unicode
// code points so markdown sees them as plain text, and we swap them back
// inside parseEntityLinks (see SAFE_PIPE / SAFE_BRACKET below).
const SAFE_PIPE = ''
const SAFE_BRACKET = ''
// Also mask URL-autolink triggers inside a marker. remark-gfm autolinks bare
// `https://…` / `www.…` URLs, which splits a marker like
// `[[suggestion:Label|scrape https://shop.com/x]]` into separate text + <a>
// nodes — so parseEntityLinks never sees the whole token and the chip renders
// as raw text (with a leftover masked-pipe glyph). Breaking the scheme / `www.`
// with a private-use char stops autolinking; unmask() strips it back out.
const SAFE_URL = ''
const preprocessMarkers = (s: string): string =>
  s.replace(/\[\[([^\]]*)\]\]/g, (m) => {
    // Only mask if it really looks like one of our markers (`Type:…`).
    if (!m.includes(':')) return m
    const inner = m.slice(2, -2)
    const masked = inner
      .replace(/\|/g, SAFE_PIPE)
      .replace(/(https?):\/\//gi, `$1${SAFE_URL}://`)
      .replace(/\bwww\./gi, `www${SAFE_URL}.`)
    return '[[' + masked + ']]'
  })

const StreamingText = ({
  content,
  isStreaming,
  components,
}: {
  content: string
  isStreaming: boolean
  components: any
}) => {
  return (
    <Box position="relative">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {preprocessMarkers(content)}
      </ReactMarkdown>
      {isStreaming && content && (
        <MotionBox
          as="span"
          display="inline-block"
          w="2px"
          h="14px"
          bg="neutral.400"
          ml={0.5}
          verticalAlign="text-bottom"
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
      )}
    </Box>
  )
}

const SuggestionChips = ({
  suggestions,
  onSendMessage,
  onInvestigateOnline,
}: {
  suggestions: NonNullable<Message['suggestions']>
  onSendMessage?: (message: string) => void
  onInvestigateOnline?: (vertexId: string) => void
}) => {
  const { isDeveloper } = useAuth().permissions
  return (
  <Box mt="12px" pt="10px" borderTop="1px solid" borderColor="gray.100">
    <Text fontSize="10px" fontWeight="700" color="gray.400" letterSpacing="0.06em" textTransform="uppercase" mb="8px">
      Suggested follow-ups
    </Text>
    <VStack align="stretch" spacing="6px">
      {suggestions.map((s, i) => (
        <Box
          key={i}
          as="button"
          type="button"
          textAlign="left"
          cursor="pointer"
          bg="white"
          px="12px"
          py="8px"
          borderRadius="10px"
          border="1px solid"
          borderColor="purple.100"
          boxShadow="0 1px 2px rgba(124, 58, 237, 0.04)"
          _hover={{
            borderColor: 'purple.300',
            bg: 'purple.50',
            transform: 'translateY(-1px)',
            boxShadow: '0 3px 8px -3px rgba(124, 58, 237, 0.18)',
          }}
          _active={{ transform: 'translateY(0)' }}
          transition="all 0.15s ease"
          onClick={() => {
            if (s.type === 'investigate-online' && onInvestigateOnline) {
              onInvestigateOnline(s.prompt)
            } else {
              onSendMessage?.(s.prompt)
            }
          }}
        >
          <HStack spacing="8px" align="flex-start">
            {/* AI star for every suggestion. The tool it uses is shown as a
                badge BELOW (e.g. the Firecrawl flame for an online investigation). */}
            <Icon as={Sparkles} w="13px" h="13px" color="purple.400" mt="2px" flexShrink={0} />
            <Box minW={0} flex="1">
              <Text fontSize="13px" fontWeight="600" color="purple.700" lineHeight="1.3">
                {s.label}
              </Text>
              <Text fontSize="11px" color="gray.500" lineHeight="1.4" mt="2px">
                {s.reasoning}
              </Text>
              {/* Online investigation is a Firecrawl tool → always show the flame,
                  regardless of what the model tagged. */}
              {isDeveloper && (
                <ToolBadges tools={s.type === 'investigate-online' ? ['firecrawl'] : s.tools} />
              )}
            </Box>
            <Icon as={ArrowUpRight} w="13px" h="13px" color="purple.300" mt="2px" flexShrink={0} ml="auto" />
          </HStack>
        </Box>
      ))}
    </VStack>
  </Box>
  )
}

const KrakenChatMessage = ({
  message,
  isUser,
  isStreaming = false,
  onSendMessage,
  onInvestigateOnline,
  suggestions,
  toolsUsed,
  socialPosts,
  stopped = false,
  printMode = false,
}: KrakenChatMessageProps) => {
  const navigate = useNavigate()
  const { isDeveloper } = useAuth().permissions
  const [thinkingSteps, setThinkingSteps] = useState<string[]>([])
  const [displayContent, setDisplayContent] = useState('')
  const [isThinking, setIsThinking] = useState(false)

  const handleEntityClickLive = useCallback(
    (type: string, id: string, name?: string) => {
      const cleanId = id.replace(/^(seller_|item_|saw_|entity_|product_|seizure_)/, '')
      const openInNewTab = (route: string) =>
        window.open(`${window.location.origin}/#${route}`, '_blank', 'noopener,noreferrer')

      switch (type.toLowerCase()) {
        case 'suggestion': {
          // Special suggestion: `[[suggestion:Label|investigate-online:<vertex_id>]]`
          // The chip's "prompt text" half (the `name` arg here) is the magic
          // payload, not free-form text. Intercept it and call the dedicated
          // discovery handler — falls back to a normal send if the host
          // didn't wire the handler.
          const payload = name ?? id
          if (payload?.startsWith('investigate-online:')) {
            const vertexId = payload.slice('investigate-online:'.length).trim()
            if (vertexId && onInvestigateOnline) {
              onInvestigateOnline(vertexId)
              return
            }
          }
          onSendMessage?.(payload)
          return
        }
        case 'seller':
          return openInNewTab(`/react/sellers/${cleanId}`)
        case 'item':
          return openInNewTab(`/react/items/${cleanId}`)
        case 'saw':
        case 'website':
          return openInNewTab(`/react/saw/sites/${cleanId}`)
        case 'entity':
        case 'customentity':
          return openInNewTab(`/react/custom-entities/entities?focus=${cleanId}`)
        case 'product':
        case 'customproduct':
          return openInNewTab(`/react/custom-entities/products?focus=${cleanId}`)
        case 'seizure':
        case 'customseizure':
          return openInNewTab(`/react/custom-entities/seizures/${cleanId}`)
        default:
          // Unknown type — fall back to opening in same tab via navigate
          // so we at least try, instead of swallowing the click.
          console.warn(`Unknown entity type: ${type}`)
          navigate(`/react/${type.toLowerCase()}/${cleanId}`)
      }
    },
    [navigate, onSendMessage, onInvestigateOnline],
  )

  // In printMode, chips/score-bars keep their visual styling but clicking
  // does nothing — this is a static PDF render, not an interactive page.
  const handleEntityClick = useCallback(
    (type: string, id: string, name?: string) => {
      if (printMode) return
      handleEntityClickLive(type, id, name)
    },
    [printMode, handleEntityClickLive],
  )

  const processChildren = useCallback(
    (children: React.ReactNode): React.ReactNode => {
      if (typeof children === 'string') {
        return parseEntityLinks(children, handleEntityClick, printMode)
      }
      if (Array.isArray(children)) {
        return children.map((child, i) =>
          typeof child === 'string' ? (
            <span key={i}>{parseEntityLinks(child, handleEntityClick, printMode)}</span>
          ) : (
            child
          ),
        )
      }
      return children
    },
    [handleEntityClick, printMode],
  )

  useEffect(() => {
    if (isUser) {
      setDisplayContent(message)
      setThinkingSteps([])
      setIsThinking(false)
      return
    }

    const isThinkingMessage =
      message?.includes('Fetching') ||
      message?.includes('Analyzing') ||
      message?.includes('Processing') ||
      message?.includes('Loading') ||
      message?.includes('Searching') ||
      message?.includes('Scanning') ||
      message?.includes('Scraping') ||
      message?.includes('Pulling') ||
      message?.includes('Pulled') ||
      message?.includes('Investigating') ||
      message?.includes('Querying') ||
      message?.includes('Capturing') ||
      message?.includes('Identifying') ||
      message?.includes('Finding') ||
      // Verbs emitted by the discover/expand ("Investigate online") stream —
      // e.g. "Step 1/1: preparing search queries…", "Inspecting top 15 results…"
      message?.includes('Step ') ||
      message?.includes('Building') ||
      message?.includes('Evaluating') ||
      message?.includes('Verifying') ||
      message?.includes('Inspecting') ||
      message?.includes('Reading') ||
      message?.includes('Reasoning') ||
      message?.includes('Creating') ||
      message?.includes('Thinking')

    if (isStreaming && !message) {
      setIsThinking(true)
      setThinkingSteps(['Thinking'])
      setDisplayContent('')
    } else if (isStreaming && isThinkingMessage) {
      setIsThinking(true)
      const cleanMessage = message
        .replace(/^[🔍🧠⚙️💭]\s*/, '')
        // Drop a redundant leading status-verb prefix (e.g. the FAB streams
        // every step as "Investigating: …") so each thinking step reads as
        // clean plain text — matching the /octopus chat's thinking chain.
        .replace(/^(Investigating|Searching|Scraping|Analyzing|Processing|Querying|Capturing|Identifying|Finding|Fetching|Loading)\s*:\s*/i, '')
        .trim()
      setThinkingSteps((prev) => {
        if (prev[prev.length - 1] === cleanMessage) return prev
        return [...prev, cleanMessage]
      })
      setDisplayContent('')
    } else if (message) {
      setIsThinking(false)
      setDisplayContent(message)
    }
  }, [isStreaming, message, isUser])

  const markdownComponents = {
    p: ({ node, children }: any) => {
      // Image-only paragraphs (e.g. a run of scraped social-post thumbnails)
      // render as a transparent (display:contents) wrapper so the inline
      // thumbnails flow together into one wrapping row instead of each
      // paragraph forcing its own line.
      const kids = node?.children ?? []
      const onlyImages =
        kids.length > 0 &&
        kids.every((c: any) => c.type === 'image' || (c.type === 'text' && !String(c.value ?? '').trim()))
      if (onlyImages) {
        return <Box sx={{ display: 'contents' }}>{processChildren(children)}</Box>
      }
      return (
        <Text mb={2} mt={1} color="neutral.800" lineHeight="1.6">
          {processChildren(children)}
        </Text>
      )
    },
    strong: ({ children }: any) => (
      <Text as="strong" fontWeight="600" color="neutral.800">
        {processChildren(children)}
      </Text>
    ),
    em: ({ children }: any) => (
      <Text as="em" fontStyle="italic" color="neutral.700">
        {processChildren(children)}
      </Text>
    ),
    ul: ({ children }: any) => (
      <Box as="ul" pl={5} mb={2}>
        {children}
      </Box>
    ),
    ol: ({ children }: any) => (
      <Box as="ol" pl={5} mb={2}>
        {children}
      </Box>
    ),
    li: ({ children }: any) => (
      <Text as="li" mb={1} color="neutral.800">
        {processChildren(children)}
      </Text>
    ),
    h1: ({ children }: any) => (
      <Text as="h1" fontSize="xl" fontWeight="600" mb={2} mt={3} color="neutral.800">
        {processChildren(children)}
      </Text>
    ),
    h2: ({ children }: any) => (
      <Text as="h2" fontSize="lg" fontWeight="600" mb={2} mt={3} color="neutral.800">
        {processChildren(children)}
      </Text>
    ),
    h3: ({ children }: any) => (
      <Text as="h3" fontSize="md" fontWeight="600" mb={1.5} mt={2} color="neutral.800">
        {processChildren(children)}
      </Text>
    ),
    table: ({ children }: any) => (
      <Box overflowX="auto" my={3} borderRadius="8px" border="1px solid" borderColor="neutral.200">
        <Box as="table" w="full" fontSize="13px" css={{ borderCollapse: 'collapse' }}>
          {children}
        </Box>
      </Box>
    ),
    thead: ({ children }: any) => (
      <Box as="thead" bg="neutral.50">
        {children}
      </Box>
    ),
    tbody: ({ children }: any) => <Box as="tbody">{children}</Box>,
    tr: ({ children }: any) => (
      <Box as="tr" borderBottom="1px solid" borderColor="neutral.100" _last={{ borderBottom: 'none' }}>
        {children}
      </Box>
    ),
    th: ({ children }: any) => (
      <Box as="th" px={3} py={2} fontWeight="600" color="neutral.700" fontSize="12px" textAlign="left">
        {processChildren(children)}
      </Box>
    ),
    td: ({ children }: any) => (
      <Box as="td" px={3} py={2} color="neutral.800" verticalAlign="top">
        {processChildren(children)}
      </Box>
    ),
    img: ({ src, alt }: any) => {
      // Screenshots are enforcement evidence — keep them large and uncropped.
      // Everything else (scraped social-post images) renders as a tidy, uniform
      // inline thumbnail so a set of them flows into an aligned row of cards
      // instead of giant stacked full-width portraits. `no-referrer` lets
      // hotlink-protected social CDN images actually load.
      const isScreenshot = /^\s*screenshot\b/i.test(alt || '')
      if (isScreenshot) {
        return (
          <Box my={2} borderRadius="8px" overflow="hidden" border="1px solid" borderColor="neutral.200" maxW="100%">
            <Box
              as="img"
              src={src}
              alt={alt || ''}
              referrerPolicy="no-referrer"
              maxW="100%"
              maxH="400px"
              objectFit="contain"
              display="block"
              bg="neutral.50"
            />
            {alt && (
              <Text fontSize="11px" color="neutral.500" px={2} py={1} bg="neutral.50">
                {alt}
              </Text>
            )}
          </Box>
        )
      }
      return (
        <Box
          as={src ? 'a' : 'div'}
          {...(src ? { href: src, target: '_blank', rel: 'noopener noreferrer' } : {})}
          display="inline-flex"
          flexDirection="column"
          verticalAlign="top"
          mr="8px"
          mb="8px"
          w="fit-content"
          maxW="100%"
          borderRadius="12px"
          overflow="hidden"
          border="1px solid"
          borderColor="neutral.200"
          bg="neutral.50"
          boxShadow="0 1px 3px rgba(0,0,0,0.08)"
          transition="border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease"
          _hover={{ borderColor: KRAKEN_PRIMARY, boxShadow: '0 6px 16px rgba(0,0,0,0.12)', transform: 'translateY(-1px)' }}
        >
          <Box
            as="img"
            src={src}
            alt={alt || ''}
            referrerPolicy="no-referrer"
            h="160px"
            w="auto"
            maxW="240px"
            minW="120px"
            objectFit="cover"
            display="block"
            bg="neutral.100"
          />
          {alt && (
            <Text fontSize="11px" color="neutral.600" fontWeight={500} px="9px" py="6px" maxW="240px" noOfLines={1}>
              {alt}
            </Text>
          )}
        </Box>
      )
    },
    a: ({ href, children }: any) => (
      <Text
        as="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        color={KRAKEN_PRIMARY}
        fontWeight={500}
        borderBottom="1px dashed"
        borderColor="purple.300"
        _hover={{ borderColor: KRAKEN_PRIMARY, bg: 'purple.50' }}
        transition="all 0.15s ease"
      >
        {processChildren(children)}
      </Text>
    ),
    code: ({ children, className }: any) => {
      const isInline = !className
      if (isInline) {
        return (
          <Text
            as="code"
            px={1.5}
            py={0.5}
            bg="neutral.100"
            borderRadius="4px"
            fontSize="13px"
            fontFamily="mono"
            color="neutral.700"
          >
            {children}
          </Text>
        )
      }
      return (
        <Box as="pre" p={3} bg="neutral.800" borderRadius="8px" overflowX="auto" my={2}>
          <Text as="code" fontSize="13px" fontFamily="mono" color="white">
            {children}
          </Text>
        </Box>
      )
    },
  }

  return (
    <VStack align="stretch" gap={1.5} role="group" w="full">
      {/* In printMode every message reads as a full-width, left-aligned block
          inside its report card (document rhythm). Live chat keeps the user
          message right-aligned in a compact bubble. */}
      <Box
        w="full"
        display="flex"
        justifyContent={printMode ? 'flex-start' : isUser ? 'flex-end' : 'flex-start'}
        position="relative"
      >
        <Box maxW={printMode || !isUser ? '100%' : '70%'}>
          {isUser && printMode ? (
            // Document-style: the user's words render as plain left-aligned
            // content (no chat bubble) so it sits cleanly in the report card.
            <Box color="neutral.800" fontSize="14px" lineHeight="1.6" fontWeight={500}>
              <Text fontWeight={500}>
                {parseEntityLinks(displayContent, handleEntityClick, printMode)}
              </Text>
            </Box>
          ) : isUser ? (
            <Box
              px={4}
              py={3}
              borderRadius="20px"
              bg="secondary.400"
              color="white"
              fontSize="14px"
              lineHeight="1.5"
              fontWeight={500}
            >
              <Text fontWeight={500} whiteSpace="pre-wrap">
                {parseEntityLinks(displayContent, handleEntityClick, printMode)}
              </Text>
            </Box>
          ) : printMode ? (
            // Static render — no entrance animation, no thinking chain, no
            // streaming cursor, no suggestion chips. Same markdownComponents
            // as the live chat so tables/lists/code/images/chips render
            // identically, just non-interactive.
            <Box color="neutral.800" fontSize="14px" lineHeight="1.6" fontWeight={400}>
              {displayContent && (
                <StreamingText content={displayContent} isStreaming={false} components={markdownComponents} />
              )}
            </Box>
          ) : (
            <Box color="neutral.800" fontSize="14px" lineHeight="1.6" fontWeight={400}>
              {/* Scraped-post grid renders as part of the response (top of the
                  body), not a separate section tacked on at the bottom. */}
              {!isUser && socialPosts && socialPosts.length > 0 && (
                <SocialPostGrid posts={socialPosts} />
              )}
              <AnimatePresence mode="wait">
                {isThinking && thinkingSteps.length > 0 ? (
                  <ThinkingChain key="thinking" steps={thinkingSteps} />
                ) : displayContent ? (
                  <MotionBox
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <StreamingText content={displayContent} isStreaming={isStreaming} components={markdownComponents} />
                  </MotionBox>
                ) : null}
              </AnimatePresence>
              {!isUser && stopped && (
                <HStack gap="6px" mt="8px" color="neutral.500" aria-live="polite">
                  <Icon as={CircleStop} boxSize="13px" aria-hidden />
                  <Text fontSize="12px" fontWeight={500}>
                    Stopped by you
                  </Text>
                </HStack>
              )}
              {!isUser && !isStreaming && suggestions && suggestions.length > 0 && (
                <SuggestionChips
                  suggestions={suggestions}
                  onSendMessage={onSendMessage}
                  onInvestigateOnline={onInvestigateOnline}
                />
              )}
              {!isUser && !isStreaming && isDeveloper && toolsUsed && toolsUsed.length > 0 && (
                <Box mt="10px" pt="8px" borderTop="1px solid" borderColor="gray.100">
                  <ToolBadges tools={toolsUsed} label="Tools used" />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </VStack>
  )
}

export default KrakenChatMessage
